import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Coins, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useWorkspace } from "../../lib/WorkspaceContext";
import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from "../../lib/money";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

/**
 * QENVA · Workspace currency setting.
 *
 * Structural, workspace-wide setting (same nature as `plan`) — not a
 * notification channel, but hosted here per product decision. Owner-only:
 * viewers/members/admins see the current value read-only.
 *
 * Endpoint: PATCH /workspaces/{id}/currency (owner-only, validated against
 * SUPPORTED_CURRENCIES on the backend too).
 */
export default function CurrencySection() {
  const { t } = useTranslation("common");
  const { current, currentId, refresh } = useWorkspace();
  const [saving, setSaving] = useState(false);

  const isOwner = current?.role === "owner";
  const currency = current?.currency || DEFAULT_CURRENCY;

  const handleChange = async (nextCurrency) => {
    if (!nextCurrency || nextCurrency === currency) return;
    if (!window.confirm(t("settings.currency.confirm", { from: currency, to: nextCurrency }))) return;
    setSaving(true);
    try {
      const r = await api.patch(`/workspaces/${currentId}/currency`, { currency: nextCurrency });
      toast.success(t("settings.currency.saved", {
        previous: r.data.previous_currency,
        next: r.data.currency,
      }));
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("settings.currency.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="border border-zinc-200 rounded-2xl bg-white p-6 mb-6"
      data-testid="currency-section"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700">
            <Coins size={18} strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-950">
              {t("settings.currency.title")}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-md">
              {t("settings.currency.subtitle")}
            </p>
          </div>
        </div>
      </div>

      {isOwner ? (
        <div className="flex items-center gap-3" data-testid="currency-select-wrap">
          <Select value={currency} onValueChange={handleChange} disabled={saving}>
            <SelectTrigger className="w-40" data-testid="currency-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_CURRENCIES.map((code) => (
                <SelectItem key={code} value={code} data-testid={`currency-option-${code}`}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {saving && <Loader2 size={14} className="animate-spin text-zinc-400" />}
        </div>
      ) : (
        <div
          className="bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2 text-[12px] text-zinc-700 inline-flex items-center gap-2"
          data-testid="currency-readonly"
        >
          <span className="font-medium">{t("settings.currency.current_label")}:</span>
          <code className="font-mono text-zinc-600" data-testid="currency-readonly-value">{currency}</code>
          <span className="text-zinc-400">— {t("settings.currency.owner_only_hint")}</span>
        </div>
      )}
    </section>
  );
}
